import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  listManual, updateManualSection, createManualSection, deleteManualSection,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Pencil, Save, X, Plus, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Manual() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listManual();
      setSections(data);
    } catch {
      toast.error("Error cargando manual");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const current = useMemo(() => {
    if (!sections.length) return null;
    if (slug) return sections.find((s) => s.slug === slug) || sections[0];
    return sections[0];
  }, [sections, slug]);

  useEffect(() => {
    if (current) {
      setEditTitle(current.title);
      setEditContent(current.content);
      setEditing(false);
    }
  }, [current?.slug]);

  const handleSave = async () => {
    try {
      await updateManualSection(current.slug, { title: editTitle, content: editContent });
      toast.success("Sección guardada");
      setEditing(false);
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleCreate = async () => {
    const slugClean = newSlug.trim().toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    if (!slugClean || !newTitle.trim()) {
      toast.error("Slug y título son obligatorios");
      return;
    }
    try {
      const maxOrder = sections.reduce((m, s) => Math.max(m, s.order || 0), 0);
      await createManualSection({
        slug: slugClean,
        title: newTitle.trim(),
        content: `# ${newTitle.trim()}\n\nEscribe aquí…`,
        order: maxOrder + 10,
      });
      toast.success("Sección creada");
      setAddingNew(false);
      setNewSlug("");
      setNewTitle("");
      await load();
      nav(`/manual/${slugClean}`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteManualSection(toDelete.slug);
      toast.success("Sección eliminada");
      setToDelete(null);
      const remaining = sections.filter((s) => s.slug !== toDelete.slug);
      setSections(remaining);
      if (current?.slug === toDelete.slug && remaining.length) {
        nav(`/manual/${remaining[0].slug}`);
      }
    } catch {
      toast.error("Error eliminando sección");
    }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm py-12 text-center">Cargando manual…</div>;
  }

  return (
    <div data-testid="manual-page" className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
      {/* Sidebar */}
      <aside className="lg:sticky lg:top-8 self-start">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={18} className="text-slate-700" strokeWidth={1.8} />
          <h2 className="font-cabinet text-lg font-bold text-slate-900">Manual</h2>
        </div>
        <nav className="flex flex-col gap-0.5">
          {sections.map((s) => (
            <Link
              key={s.slug}
              to={`/manual/${s.slug}`}
              data-testid={`manual-nav-${s.slug}`}
              className={`px-3 py-2 text-xs rounded-md transition-colors border-l-2 ${
                current?.slug === s.slug
                  ? "border-slate-900 bg-slate-100 text-slate-900 font-semibold"
                  : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {s.title}
            </Link>
          ))}
        </nav>
        <Button
          onClick={() => setAddingNew(true)}
          variant="outline"
          size="sm"
          className="mt-4 w-full text-xs"
          data-testid="manual-add-section-btn"
        >
          <Plus size={13} className="mr-1.5" /> Añadir sección
        </Button>
      </aside>

      {/* Content */}
      <main className="min-w-0">
        {!current ? (
          <div className="text-slate-400 text-sm py-12 text-center">
            Aún no hay secciones. Pulsa "Añadir sección".
          </div>
        ) : editing ? (
          <div className="space-y-4">
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="font-cabinet text-2xl font-bold tracking-tight border-0 border-b border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-slate-900"
              data-testid="manual-edit-title"
            />
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="font-mono text-[13px] leading-relaxed min-h-[480px] resize-y"
              data-testid="manual-edit-content"
              placeholder="Escribe en markdown…"
            />
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800" data-testid="manual-save-btn">
                <Save size={14} className="mr-2" /> Guardar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditTitle(current.title);
                  setEditContent(current.content);
                  setEditing(false);
                }}
                data-testid="manual-cancel-btn"
              >
                <X size={14} className="mr-2" /> Cancelar
              </Button>
              <span className="text-[10px] text-slate-400 ml-auto">
                Usa markdown: **negrita**, `código`, [enlaces](url), tablas, listas, etc.
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">
                Sección · {current.slug}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  data-testid="manual-edit-btn"
                >
                  <Pencil size={13} className="mr-1.5" /> Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setToDelete(current)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid="manual-delete-btn"
                >
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
            <article className="prose prose-slate prose-sm max-w-none prose-headings:font-cabinet prose-headings:tracking-tight prose-h1:text-3xl prose-h1:font-bold prose-h2:text-xl prose-h2:mt-8 prose-h3:text-base prose-table:text-xs prose-th:bg-slate-50 prose-td:px-3 prose-td:py-1.5 prose-th:px-3 prose-th:py-2 prose-code:text-[0.85em] prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-blockquote:border-l-2 prose-blockquote:border-slate-300 prose-blockquote:not-italic prose-blockquote:text-slate-600">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.content}</ReactMarkdown>
            </article>
            <div className="text-[10px] text-slate-400 pt-6 border-t border-slate-100">
              Última actualización: {new Date(current.updated_at).toLocaleString("es-ES")}
            </div>
          </div>
        )}
      </main>

      {/* New section dialog */}
      <AlertDialog open={addingNew} onOpenChange={setAddingNew}>
        <AlertDialogContent data-testid="manual-new-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Añadir nueva sección</AlertDialogTitle>
            <AlertDialogDescription>
              Crea una nueva sección del manual. El slug se usa en la URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Título</label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Ej: Cómo cobrar una factura"
                data-testid="manual-new-title"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Slug (URL)</label>
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="ej: cobrar-factura"
                data-testid="manual-new-slug"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-new-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="manual-new-confirm">
              Crear sección
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sección?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{toDelete?.title}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="manual-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
